import React from 'react'
import { Link } from 'gatsby'
import {
  Box, Button, SxProps, Typography, useTheme 
} from '@mui/material'

import { Icon } from './Icon'

import { TimeAndDuration } from '@components/TimeAndDuration'
import { rudderslabTrackOnClick } from '~/utils/common'

export type ContentProps = {
  node: {
    title: string
    slug: { current: string }
    blog_category?: string
    blog_image?: { asset: { url: string } }
    blog_authors?: {
      author_name: string
      author_position: string
      author_image: { asset: { url: string } }
    }[]
    short_description?: string
    listing_image?: { asset: { url: string } }
    shortdescription?: string
    youtube?: string
    date: string
    time: string
    duration: string
  }
}

type FeaturedContentProps = {
  data: ContentProps
  type?: 'blog' | 'customers' | 'events'
  sx?: SxProps
}

export const FeaturedContent = ({ data, type, sx }: FeaturedContentProps) => {
  const { palette } = useTheme()

  const author = data.node.blog_authors?.[0]

  return (
    <Box sx={{ maxWidth: 1152, mx: 'auto', ...sx }} data-test="featured-block">
      <Box
        component={Link}
        to={`/${type}/${data.node.slug.current}`}
        sx={{
          bgcolor: 'background.default',
          borderRadius: 6,
          boxShadow: 1,
          cursor: 'pointer',
          display: 'flex',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            width: '45%',
            maxWidth: 440,
            pb: 4,
            pt: { xs: 3.5, laptop: 6 },
            px: { xs: 3.5, laptop: 6 },
          }}>
          <Typography
            variant='subtitle2'
            sx={{ color: 'primary.main', fontWeight: 700 }}>
            {data.node.blog_category?.toUpperCase()}
          </Typography>

          <Typography
            variant='h1'
            sx={{
              fontSize: { xs: 22, laptop: 28 },
              mt: { xs: 1.5, laptop: 2 },
            }}>
            {data.node.title}
          </Typography>

          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', mt: { xs: 1.5, laptop: 2 } }}>
            {data.node.short_description || data.node.shortdescription}
          </Typography>

          {type === 'blog' && author && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                mt: { xs: 3, laptop: 3.5 },
              }}>
              <Box>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                  {author.author_name}
                </Typography>
                <Typography
                  variant='subtitle2'
                  sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                  {author.author_position}
                </Typography>
              </Box>

              <Box
                component='img'
                src={author.author_image.asset?.url}
                alt={author.author_name}
                sx={{
                  width: { xs: 42, laptop: 68 },
                  height: { xs: 42, laptop: 68 },
                  borderRadius: '50%',
                  filter: 'grayscale(1)',
                  ml: 2,
                }}
              />
            </Box>
          )}

          {type === 'events' && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                mt: { xs: 3, laptop: 3.5 },
              }}>
              <Button
                variant='contained'
                color='primary'
                data-test='button-tag'
                data-link={data.node.slug.current}
                sx={{
                  width: 150,
                  background:
                    'linear-gradient(135.55deg, #3E35D1 6.09%, #86F8E0 116.03%)',
                  borderRadius: 2,
                  color: 'background.default',
                  fontSize: { xs: 14, laptop: 16 },
                  fontWeight: 700,
                  py: 1.25,
                }}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  rudderslabTrackOnClick('button', data.node.title, e)
                }}>
                {data.node.youtube ? 'Watch Now' : 'Register Now'}
              </Button>

              <Box sx={{ ml: 2 }}>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <Icon icon='calendar' color={palette.scrollBar.main} />
                  <Typography
                    sx={{ color: 'scrollBar.main', fontWeight: 700, ml: 1 }}>
                    {data.node.date}
                  </Typography>
                </Box>

                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <TimeAndDuration data={data?.node} />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          component='img'
          src={(data.node.blog_image || data.node.listing_image)?.asset?.url}
          alt={data.node.title}
          sx={{
            width: '55%',
            flex: 1,
            objectFit: 'cover',
            ...(type === 'events' && { objectPosition: 'top left' }),
          }}
        />
      </Box>
    </Box>
  )
}
